import React, { useState, useMemo } from "react";
import { Badge } from "@/components/ui/badge";
import {
  Plus,
  Pencil,
  UserRoundCog,
  StickyNote,
  CheckCircle2,
  RotateCcw,
  Download,
  BellRing,
  Archive,
  FileText,
  Phone,
  Receipt,
  Wallet,
  Calendar as CalendarIcon,
} from "lucide-react";

/**
 * HistoryPanel — compact, append-only timeline of audit events.
 *
 * Render rule (per office spec):
 *   - newest events on TOP (events list is reversed at render time)
 *   - compact one-line-per-event with action badge + relative metadata
 *   - long detail text is clamped to 2 lines (full text available via
 *     a tooltip on hover for power users)
 *   - 16/07/2026: icona lucide-react accanto al badge azione per
 *     riconoscere la sorgente a colpo d'occhio (Timeline Cliente 360°).
 *
 * Reused across Pratiche Quotidiane and Diario (Phase 3 plan).
 *
 * Each event item shape (matches backend `_build_daily_audit_entry` /
 *   `_build_audit_entry`):
 *     { operatorId, operatorName, userId, timestamp, action, details? }
 *
 * Props
 *   events    — array as stored in `practice.history` (creation order)
 *   className — extra wrapper classes
 *   testId    — optional data-testid for the root element
 */
const ACTION_LABEL = {
  created:        { label: "Creata",        color: "bg-blue-100 text-blue-800 border-blue-200",         Icon: Plus },
  edited:         { label: "Modificata",    color: "bg-slate-100 text-slate-700 border-slate-200",      Icon: Pencil },
  reassigned:     { label: "Riassegnata",   color: "bg-purple-100 text-purple-800 border-purple-200",   Icon: UserRoundCog },
  note_added:     { label: "Nota",          color: "bg-amber-100 text-amber-800 border-amber-200",      Icon: StickyNote },
  closed:         { label: "Chiusa",        color: "bg-green-100 text-green-800 border-green-200",      Icon: CheckCircle2 },
  reopened:       { label: "Riaperta",      color: "bg-orange-100 text-orange-800 border-orange-200",   Icon: RotateCcw },
  exported:       { label: "Esportata",     color: "bg-cyan-100 text-cyan-800 border-cyan-200",         Icon: Download },
  notification_completed: { label: "Completata da notifica", color: "bg-emerald-100 text-emerald-800 border-emerald-200", Icon: BellRing },
  diary_completed:        { label: "Completata",             color: "bg-emerald-100 text-emerald-800 border-emerald-200", Icon: CheckCircle2 },
  diary_reopened:         { label: "Riaperta",               color: "bg-orange-100 text-orange-800 border-orange-200",    Icon: RotateCcw },
  auto_archived:          { label: "Archiviata (informativa)", color: "bg-slate-200 text-slate-700 border-slate-300",     Icon: Archive },
  // 16/07/2026 — Sorgenti aggregate per Timeline Cliente 360°.
  // Non sempre presenti come `action` del payload backend attuale, ma
  // supportate qui per copertura totale della roadmap.
  call:            { label: "Telefonata",   color: "bg-sky-100 text-sky-800 border-sky-200",            Icon: Phone },
  call_added:      { label: "Telefonata",   color: "bg-sky-100 text-sky-800 border-sky-200",            Icon: Phone },
  practice_created:{ label: "Pratica",      color: "bg-indigo-100 text-indigo-800 border-indigo-200",   Icon: FileText },
  practice:        { label: "Pratica",      color: "bg-indigo-100 text-indigo-800 border-indigo-200",   Icon: FileText },
  invoice:         { label: "Fattura",      color: "bg-rose-100 text-rose-800 border-rose-200",         Icon: Receipt },
  invoice_created: { label: "Fattura",      color: "bg-rose-100 text-rose-800 border-rose-200",         Icon: Receipt },
  invoice_paid:    { label: "Fattura pag.", color: "bg-emerald-100 text-emerald-800 border-emerald-200",Icon: Receipt },
  payment:         { label: "Incasso",      color: "bg-emerald-100 text-emerald-800 border-emerald-200",Icon: Wallet },
  payment_added:   { label: "Incasso",      color: "bg-emerald-100 text-emerald-800 border-emerald-200",Icon: Wallet },
  deadline:        { label: "Scadenza",     color: "bg-violet-100 text-violet-800 border-violet-200",   Icon: CalendarIcon },
  deadline_added:  { label: "Scadenza",     color: "bg-violet-100 text-violet-800 border-violet-200",   Icon: CalendarIcon },
};

function formatTimestamp(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString("it-IT", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export function HistoryPanel({ events = [], className = "", testId, showSourceFilter = true, onCaseClick = null }) {
  // 16/07/2026 — Filtro sorgente per la Timeline. Chip cliccabili sopra
  // la lista per mostrare solo eventi di un certo tipo (Telefonate,
  // Fatture, Incassi, Pratiche, Scadenze, Note, Altro).
  const [sourceFilter, setSourceFilter] = useState("all");

  // Classifica ogni event in una categoria filtrabile.
  const categorize = (action) => {
    const a = String(action || "").toLowerCase();
    if (a.startsWith("call")) return "call";
    if (a.startsWith("invoice")) return "invoice";
    if (a.startsWith("payment")) return "payment";
    if (a.startsWith("practice")) return "practice";
    if (a.startsWith("deadline")) return "deadline";
    if (a === "note_added" || a === "note") return "note";
    return "other";
  };

  const counts = useMemo(() => {
    const c = { all: 0, call: 0, invoice: 0, payment: 0, practice: 0, deadline: 0, note: 0, other: 0 };
    (events || []).forEach((ev) => {
      c.all += 1;
      c[categorize(ev.action)] += 1;
    });
    return c;
  }, [events]);

  const chipDefs = [
    { key: "all",      label: "Tutto",       activeCls: "bg-slate-800 text-white",   idleCls: "bg-white text-slate-700 border-slate-300 hover:bg-slate-50" },
    { key: "call",     label: "Telefonate",  activeCls: "bg-sky-600 text-white",     idleCls: "bg-white text-sky-700 border-sky-300 hover:bg-sky-50" },
    { key: "invoice",  label: "Fatture",     activeCls: "bg-rose-600 text-white",    idleCls: "bg-white text-rose-700 border-rose-300 hover:bg-rose-50" },
    { key: "payment",  label: "Incassi",     activeCls: "bg-emerald-600 text-white", idleCls: "bg-white text-emerald-700 border-emerald-300 hover:bg-emerald-50" },
    { key: "practice", label: "Pratiche",    activeCls: "bg-indigo-600 text-white",  idleCls: "bg-white text-indigo-700 border-indigo-300 hover:bg-indigo-50" },
    { key: "deadline", label: "Scadenze",    activeCls: "bg-violet-600 text-white",  idleCls: "bg-white text-violet-700 border-violet-300 hover:bg-violet-50" },
    { key: "note",     label: "Note",        activeCls: "bg-amber-600 text-white",   idleCls: "bg-white text-amber-700 border-amber-300 hover:bg-amber-50" },
    { key: "other",    label: "Altro",       activeCls: "bg-slate-600 text-white",   idleCls: "bg-white text-slate-700 border-slate-300 hover:bg-slate-50" },
  ];

  if (!Array.isArray(events) || events.length === 0) {
    return (
      <div className={`text-xs text-slate-500 italic ${className}`} data-testid={testId}>
        Nessun evento registrato.
      </div>
    );
  }
  // Filtra + Reverse così l'evento più recente è in cima.
  const filtered = sourceFilter === "all"
    ? events
    : events.filter((ev) => categorize(ev.action) === sourceFilter);
  const ordered = [...filtered].reverse();
  return (
    <div className={`space-y-3 ${className}`} data-testid={testId}>
      {showSourceFilter && (
        <div className="flex flex-wrap items-center gap-1.5" data-testid={testId ? `${testId}-filter-chips` : "history-filter-chips"}>
          {chipDefs
            .filter((c) => c.key === "all" || counts[c.key] > 0)
            .map((c) => {
              const isActive = sourceFilter === c.key;
              return (
                <button
                  key={c.key}
                  type="button"
                  onClick={() => setSourceFilter(c.key)}
                  className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[11px] font-medium transition-colors ${isActive ? c.activeCls + " border-transparent" : c.idleCls}`}
                  data-testid={`history-chip-${c.key}`}
                >
                  <span>{c.label}</span>
                  <span className={`inline-flex items-center justify-center rounded-full px-1.5 min-w-[18px] text-[10px] font-semibold ${isActive ? "bg-white/25" : "bg-slate-100"}`}>
                    {counts[c.key]}
                  </span>
                </button>
              );
            })}
        </div>
      )}

      {ordered.length === 0 ? (
        <div className="text-xs text-slate-500 italic pl-3">Nessun evento in questa categoria.</div>
      ) : ordered.map((ev, i) => {
        const meta = ACTION_LABEL[ev.action] || {
          label: ev.action || "evento",
          color: "bg-slate-100 text-slate-700 border-slate-200",
          Icon: null,
        };
        const IconComp = meta.Icon;
        return (
          <div
            key={`${ev.timestamp}-${i}`}
            className="flex items-start gap-2 text-xs border-l-2 border-slate-200 pl-3"
            data-testid={`history-event-${i}`}
          >
            <Badge variant="outline" className={`${meta.color} text-[10px] uppercase tracking-wide inline-flex items-center gap-1 whitespace-nowrap`}>
              {IconComp && <IconComp className="h-3 w-3" aria-hidden="true" />}
              {meta.label}
            </Badge>
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-1 text-slate-600">
                <span className="font-medium text-slate-800">
                  {ev.operatorName || ev.operatorId || "—"}
                </span>
                <span>·</span>
                <span>{formatTimestamp(ev.timestamp)}</span>
                {/* Deep-link opzionale alla pratica di origine. Presente
                    solo se il caller (Client360Dialog v1.4.1) fornisce
                    onCaseClick e l'evento ha caseNumber/caseId. */}
                {onCaseClick && ev.caseNumber && ev.caseId && (
                  <>
                    <span>·</span>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); onCaseClick(ev.caseId, ev.caseNumber, ev); }}
                      className="inline-flex items-center gap-0.5 text-indigo-700 hover:text-indigo-900 hover:underline font-medium"
                      title={`Apri pratica ${ev.caseNumber}`}
                      data-testid={`history-open-case-${ev.caseNumber}`}
                    >
                      Apri {ev.caseNumber}
                    </button>
                  </>
                )}
              </div>
              {ev.details && (
                <p
                  className="text-slate-700 mt-0.5 line-clamp-2"
                  title={ev.details}
                >
                  {ev.details}
                </p>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default HistoryPanel;
